import React, {useCallback} from 'react';
import base64 from 'react-native-base64';
import {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  setSessionId,
  stopOneOnOneMeeting,
  stopAITeacherListening,
} from 'store/communication/actions/speechToTextServiceActions';
import World from 'components/World';
import {Platform} from 'react-native';
import LiveKitSDK from './LiveKitSDK';
import {listensToLearner} from './tutorSessionMeter';
import {LiveKitRoom, RoomAudioRenderer} from '@livekit/components-react';
import useFetchTokenLiveKit from 'hooks/useFetchTokenLiveKit';
import {v4 as uuidv4} from 'uuid';
import {LIVEKIT_WS_URL, SIMPLIFIED_FIRST_VISIT} from 'config/app.config';
import {setLastQuestion} from '../../store/course';
import {setStartLastQuestionOneToOne} from '../../store/course';
import {showMessage} from 'react-native-flash-message';
import {PROFILE_INCOMPLETE_LINE} from '../billing/billingModel';
import {
  groundedTutorContextMessage,
  isGroundedTutorUnitId,
} from 'utils/tutorContext';

const MIC_PERMISSION_DENIED_PATTERNS = [
  'notallowederror',
  'permissiondenied',
  'denied permission',
  'permission denied',
  'microphone permission',
  'securityerror',
];

const isMicrophonePermissionDeniedError = error => {
  const details = [
    error?.name,
    error?.message,
    error?.cause?.message,
    String(error || ''),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return MIC_PERMISSION_DENIED_PATTERNS.some(pattern => details.includes(pattern));
};

export const SpeechToTextService = React.memo(() => {
  const {
    token: LIVEKIT_TOKEN,
    loading,
    firstVisitFree,
    planSecondsLeft,
    getToken,
    clearToken,
    wasQuotaBlocked,
  } = useFetchTokenLiveKit();
  // console.log('SpeechToTextService getToken', LIVEKIT_TOKEN);

  const liveKitRoom = React.useRef();

  const [audioStream, setAudioStream] = useState(null);
  const audioContext = useRef(null);
  const audioSourceNode = useRef(null);
  const scriptProcessorNode = useRef(null);
  const bufferSize = 4096; // Adjust the buffer size as needed

  const courseInfo = useSelector(state => state.app.courseInfo);
  const lastQuestion = useSelector(state => state.course.lastQuestion);
  const currentLessonId = useSelector(
    state => state.course?.currentLessonProgress?.currentLessonId,
  );
  const courseTokenValues = useSelector(state => state.app.courseTokenValues);

  const AITeacherListening = useSelector(
    state => state.communication.speechToTextService.AITeacherListeningStatus,
  );
  const oneToOneMeetingStatus = useSelector(
    state => state.communication.speechToTextService.oneToOneMeetingStatus,
  );
  const livekitSessionId = useSelector(
    state => state.communication.speechToTextService.sessionId,
  );
  const tutorMode = useSelector(
    state => state.communication.speechToTextService.tutorMode || 'VOICE',
  );

  const [websocket, setWebsocket] = useState(null);
  const [websocketIntermediateResponse, setWebsocketIntermediateResponse] =
    useState('');
  const [websocketFinalResponse, setWebsocketFinalResponse] = useState('');
  const wsUrl = 'wss://cloud.fluentworlds.com:8009/';

  const dispatch = useDispatch();
  const liveKitTokenRef = useRef(null);
  const tokenErrorToastRef = useRef({lastShownAt: 0});
  const tokenFetchInFlightRef = useRef(false);
  const prevTutorStatusRef = useRef(oneToOneMeetingStatus);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);

  // A token is a metered reservation. Never prefetch one while idle: request it only when the
  // learner enters Tutor or the lesson explicitly hands off to the last-question Tutor flow.
  const getTokenMemoized = useCallback((opts = {}) => {
    const {showToastOnFailure = false, replaceExistingToken = false} = opts;
    const unitId = courseTokenValues?.unitId;
    if (!courseInfo || !isGroundedTutorUnitId(unitId)) {
      if (oneToOneMeetingStatus) dispatch(stopOneOnOneMeeting());
      if (showToastOnFailure) {
        showMessage({
          message: 'Tutor needs a lesson',
          description: groundedTutorContextMessage,
          type: 'info',
          duration: 6000,
        });
      }
      return;
    }
    {
      if (tokenFetchInFlightRef.current) return;
      tokenFetchInFlightRef.current = true;
      const session_id = uuidv4();
      dispatch(setSessionId(session_id));
      (async () => {
        if (replaceExistingToken) {
          liveKitTokenRef.current = null;
          clearToken?.();
        }

        const tokenResult = await getToken({
          ...courseInfo,
          courseId: courseTokenValues.courseId,
          unitId,
          lessonId: currentLessonId || null,
          session_id,
          lastQuestion: lastQuestion ? lastQuestion.split('|||')[0] : null,
          mode: tutorMode,
        });
        const token = tokenResult?.token || null;
        const tokenFailure = tokenResult?.error || null;

        // Store the token in a ref (fallback). Prefer hook state for render.
        liveKitTokenRef.current = token;

        if (!token) {
          if (oneToOneMeetingStatus) dispatch(stopOneOnOneMeeting());
          if (showToastOnFailure && !wasQuotaBlocked()) {
            // Non-blocking: show a toast at most once every 10s.
            const now = Date.now();
            if (now - (tokenErrorToastRef.current.lastShownAt || 0) > 10_000) {
              tokenErrorToastRef.current.lastShownAt = now;
              // A Free learner without a grade is told where to choose it, not that the tutor is broken
              // (Sol audit-free-grade #9).
              const failure = tokenFailure?.code;
              showMessage({
                message: failure === 'TUTOR_CONTEXT_REQUIRED'
                  ? 'Tutor needs a lesson'
                  : failure === 'PROFILE_INCOMPLETE'
                    ? 'Choose your grade first'
                    : 'Tutor is temporarily unavailable',
                description: failure === 'TUTOR_CONTEXT_REQUIRED'
                  ? groundedTutorContextMessage
                  : failure === 'PROFILE_INCOMPLETE'
                    ? PROFILE_INCOMPLETE_LINE
                    : 'We could not start the 1:1 tutor right now. Please try again in a moment.',
                type: 'warning',
                duration: 6000,
              });
            }
          }
          return;
        }

        if (lastQuestion) {
          dispatch(setStartLastQuestionOneToOne(true));
          dispatch(setLastQuestion(null));
        }
      })();
    }
  }, [
    courseInfo,
    courseTokenValues,
    currentLessonId,
    lastQuestion,
    tutorMode,
    oneToOneMeetingStatus,
    getToken,
    clearToken,
    wasQuotaBlocked,
    dispatch,
  ]);

  useEffect(() => {
    if (courseInfo) {
      if (lastQuestion) {
        // Lesson end triggers tutor-with-last-question behavior.
        // Even if we already have a prefetched token, we need a fresh one that includes `last_question`
        // metadata so the backend can start the correct 1:1 flow.
        getTokenMemoized({showToastOnFailure: false, replaceExistingToken: true});
      }
    }
  }, [lastQuestion]);
 
  // Ensure we always clear the in-flight flag when token state changes (success or failure).
  useEffect(() => {
    if (!loading) {
      tokenFetchInFlightRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    // On tutor OFF: discard the current reservation token. The next token is requested only
    // after the next explicit Tutor entry.
    const prev = prevTutorStatusRef.current;
    if (prev && !oneToOneMeetingStatus) {
      liveKitTokenRef.current = null;
      clearToken?.();
    }

    // On tutor ON: request the metered token now, never during idle page load.
    if (!prev && oneToOneMeetingStatus) {
      if (!(LIVEKIT_TOKEN || liveKitTokenRef.current)) {
        getTokenMemoized({showToastOnFailure: true});
      }
    }

    prevTutorStatusRef.current = oneToOneMeetingStatus;
  }, [oneToOneMeetingStatus, LIVEKIT_TOKEN, clearToken, getTokenMemoized]);

  useEffect(() => {
    console.error(AITeacherListening);
    async function getMicrophoneStream() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({audio: true});
        setAudioStream(stream);
      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
    }

    if (AITeacherListening) {
      getMicrophoneStream();
    }

    if (audioStream && !AITeacherListening) {
      audioStream.getTracks().forEach(track => track.stop());
    }
  }, [AITeacherListening]);

  useEffect(() => {
    if (!oneToOneMeetingStatus) {
      setMicPermissionDenied(false);
    }
  }, [oneToOneMeetingStatus]);

  useEffect(() => {
    if (audioStream) {
      audioContext.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      audioSourceNode.current =
        audioContext.current.createMediaStreamSource(audioStream);
      scriptProcessorNode.current = audioContext.current.createScriptProcessor(
        bufferSize,
        1,
        1,
      );

      scriptProcessorNode.current.onaudioprocess = event => {
        const inputData = event.inputBuffer.getChannelData(0);

        if (websocket && AITeacherListening) {
          const resampledData = resample(
            inputData,
            audioContext.current.sampleRate,
            16000,
          );
          const resampled16BitData = floatTo16BitPCM(resampledData);
          console.log('Sending');
          if (
            websocket.readyState == websocket.OPEN &&
            websocket.readyState != websocket.CONNECTING &&
            websocket.readyState != websocket.CLOSING
          ) {
            websocket.send(resampled16BitData);
          }

          console.log('Sent!');
        }
      };

      audioSourceNode.current.connect(scriptProcessorNode.current);
      scriptProcessorNode.current.connect(audioContext.current.destination);
    }

    return () => {
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, [audioStream, websocket, AITeacherListening]);

  useEffect(() => {
    // Web tutoring uses LiveKit below. The legacy raw-audio websocket is native-only and must not
    // create an idle connection to the retired port from the WebGL bundle.
    if (Platform.OS === 'web') return undefined;
    if (websocket == null) {
      var ws = new WebSocket(wsUrl);
      setWebsocket(ws);
    } else {
      return;
    }

    ws.onopen = () => {
      // Send the initial configuration to the WebSocket server
      const config = {
        type: 'start',
        language: 'en-US',
        format: 'raw',
        encoding: 'LINEAR16',
        sampleRateHz: 16000,
      };
      if (
        ws.readyState == ws.OPEN &&
        ws.readyState != ws.CONNECTING &&
        ws.readyState != ws.CLOSING
      ) {
        ws.send(JSON.stringify(config));
      }
    };

    ws.onmessage = event => {
      // Handle WebSocket server responses
      var webSocketResponse = event.data;
      setWebsocketIntermediateResponse(webSocketResponse);
    };

    ws.onclose = () => {
      // Handle WebSocket connection closure
      console.log('web socket closed');
      setWebsocket(null);
    };
  }, [wsUrl]);

  useEffect(() => {
    // if the webResponse is of type end, do not set the response to final response
    // and just use last set final response.
    if (
      websocketIntermediateResponse ===
      '{"type":"end","reason":"Recognition complete"}'
    ) {
      dispatch(stopAITeacherListening());
      const webSocketEncodedFinalResponse = base64.encode(
        websocketFinalResponse,
      );
      World.runCommand(
        'process_stt_final_response ' + webSocketEncodedFinalResponse,
      );
      websocket.close();
    } else {
      setWebsocketFinalResponse(websocketIntermediateResponse);
    }
  }, [websocketIntermediateResponse]);

  // Resample audio chunk function
  function resample(data, inRate, outRate) {
    var fitCount = Math.round(data.length * (outRate / inRate));
    var newData = new Array();
    var springFactor = new Number((data.length - 1) / (fitCount - 1));
    newData[0] = data[0];
    for (var i = 1; i < fitCount - 1; i++) {
      var tmp = i * springFactor;
      var before = new Number(Math.floor(tmp)).toFixed();
      var after = new Number(Math.ceil(tmp)).toFixed();
      var atPoint = tmp - before;
      newData[i] = linearInterpolate(data[before], data[after], atPoint);
    }
    newData[fitCount - 1] = data[data.length - 1];
    return newData;
  }

  function linearInterpolate(before, after, atPoint) {
    return before + (after - before) * atPoint;
  }

  function floatTo16BitPCM(input) {
    let output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      let s = Math.max(-1, Math.min(1, input[i]));
      output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return output;
  }

  const onDeviceFailure = e => {
    console.error(e);
    // In Text mode the microphone is optional: typing still works, so never block with an alert.
    // The simplified first visit types on the bar instead of stopping on a browser alert (Sol onboarding-build Q1).
    if (isMicrophonePermissionDeniedError(e) || tutorMode !== 'VOICE' || SIMPLIFIED_FIRST_VISIT) {
      setMicPermissionDenied(true);
      return;
    }
    alert(
      'Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab',
    );
  };

  if (Platform.OS === 'web') {
    return (
      <LiveKitRoom
        // Force a brand new Room instance when session id changes to avoid "old session resumes".
        key={livekitSessionId || 'livekit'}
        ref={liveKitRoom}
        // Prefer hook state so React rerenders when token arrives.
        // Keep ref as a fallback to avoid breaking any existing flow.
        token={LIVEKIT_TOKEN || liveKitTokenRef.current}
        serverUrl={LIVEKIT_WS_URL}
        // Guard: don't attempt to connect without a token; it can produce
        // confusing partial states where the websocket is up but tracks/transcription stall.
        connect={Boolean(oneToOneMeetingStatus && (LIVEKIT_TOKEN || liveKitTokenRef.current))}
        video={false}
        // Tune mic capture to be less sensitive to background noise.
        // - autoGainControl can amplify ambient noise aggressively; disable it.
        // - noiseSuppression/echoCancellation help with background + feedback.
        // - voiceIsolation is stronger but not supported on all browsers; safe to request.
        audio={
          listensToLearner(tutorMode)
            ? {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: false,
                voiceIsolation: true,
                channelCount: 1,
              }
            : false
        }
        onDeviceFailure={onDeviceFailure}
        onConnected={() => {
          console.log('LiveKit Room connected');
          setMicPermissionDenied(false);
        }}
        onDisconnected={() => {
          console.log('LiveKit Room disconnected');
        }}>
        <LiveKitSDK
          micPermissionDenied={micPermissionDenied}
          freeTourQuestion={firstVisitFree}
          planSecondsLeft={planSecondsLeft}
        />
        <RoomAudioRenderer />
      </LiveKitRoom>
    );
  }
  return <></>;
});
